import { useEffect, useState } from 'react';
import {
  filterOptions,
  renderFilterOptions,
  getExpectedFormatMessage,
  getMsg,
  getItemCount,
} from './AdvancedSearchHelpers';
import LoadingSpinner from '../../global/Loader/LoaderSpinner';
import { useNavigate } from 'react-router-dom';
import { useEnrichmentState } from '../../hooks/useEnrichmentState';
import { trackEnrichmentAction } from '../../utils/analytics';
import { getExcelColumns } from './helpers/getExcel.helpers';
import { createTXT } from './helpers/createTXT';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

const tabs = ['DISEASES', 'VARIANTS', 'GENES', 'CHEMICALS'];

interface AdvancedSearchProps {
  searchType: string;
  setSearchType: (type: string) => void;
  selectedFilter: string;
  setSelectedFilter: (filter: string) => void;
  text: string;
  handleQueryChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSearchClick: () => void;
  clearInputText: () => void;
  showExamples: () => void;
  renderSuggestions: () => JSX.Element;
  loading: boolean;
  suggestionsData: any[];
  handleBackClick: () => void;
  handleSearchSelectedItems: () => void;
  handleSelectAll: () => void;
  handleClearAll: () => void;
  showTextarea: boolean;
  validateInput: (input: any, searchType: string) => boolean;
  selectedItems: any[];
}

const AdvancedSearch = ({
  searchType,
  setSearchType,
  selectedFilter,
  setSelectedFilter,
  text,
  handleQueryChange,
  handleSearchClick,
  clearInputText,
  showExamples,
  renderSuggestions,
  loading,
  suggestionsData,
  handleBackClick,
  handleSearchSelectedItems,
  handleSelectAll,
  handleClearAll,
  showTextarea,
  validateInput,
  selectedItems,
}: AdvancedSearchProps) => {
  const navigate = useNavigate();
  const { setEnrichmentFrom } = useEnrichmentState();
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [metadataContent, setMetadataContent] = useState<string | null>(null);

  const [showExamplesButton, setShowExamplesButton] = useState<boolean>(true);

  const showExamplesOnClick = () => {
    showExamples();
    setShowExamplesButton(false);
  };

  const clearListOnClick = () => {
    clearInputText();
    setShowExamplesButton(true);
  }

  useEffect(() => {
    if (showTextarea && !text.trim()) {
      setShowExamplesButton(true);
    }
  }, [showTextarea, text]);

  useEffect(() => {
    const availableFilters = filterOptions[searchType] || [];
    
    if (availableFilters.length > 0) {
      if (!availableFilters.find(filter => filter.value === selectedFilter)) {
        setSelectedFilter(availableFilters[0].value);
      }
    }
  }, [searchType, setSelectedFilter, selectedFilter]);

  const handleEnrichmentRedirect = () => {
    trackEnrichmentAction('enrichment_analysis_click', 'advanced_search');

    const enrichmentFrom = {
      from: 'advancedSearch',
      searchType,
      selectedFilter,
      text,
    };

    setEnrichmentFrom(enrichmentFrom);

    navigate('/enrichment', { state: enrichmentFrom });
  };

  const filteredNotFoundItems = suggestionsData.filter((item: any) => {
    return item.validationError || !validateInput(item.query, searchType);
  });

  const uniqueResults = new Set();
  const uniqueQueries = new Set();

  suggestionsData.forEach((item: any) => {
    uniqueQueries.add(item.query);

    if (!item.validationError && item.found && Array.isArray(item.results)) {
      item.results.forEach((result: any) => {
        uniqueResults.add(JSON.stringify(result));
      });
    }
  });

  const totalSearched = uniqueQueries.size;
  const totalFound = uniqueResults.size;

  const handleDownload = async () => {
    try {
      const rows =
        suggestionsData?.flatMap((item: any) =>
          item.results?.map((r: any) => ({
            ...r,
            __query: item.query,
          })),
        ) || [];

      const columns = getExcelColumns(searchType, selectedFilter);

      const dataForExcel = rows.map((row: any) => {
        const excelRow: any = {};

        columns.forEach((col: any) => {
          excelRow[col.header] = col.value(row, row.__query);
        });

        return excelRow;
      });

      // Create Excel
      const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Results');

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const metadataContent = createTXT({
        searchType,
        selectedFilter,
        text,
        totalSearched,
        totalFound,
      });

      setMetadataContent(metadataContent);

      // Create ZIP
      const zip = new JSZip();
      zip.file(`advanced_search_${searchType.toLowerCase()}_results.xlsx`, excelBuffer);
      zip.file(`advanced_search_${searchType.toLowerCase()}_metadata.txt`, metadataContent);

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Download ZIP
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `advanced_search_${searchType.toLowerCase()}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading results:', error);
      setShowErrorAlert(true);
    }
  };

  return (
    <>
      <div className="row">
        <div className="multiple-filters">
          <div
            className="ms-radio-group ms-radio-group-mobile"
            onChange={(e: React.ChangeEvent<HTMLDivElement>) =>
              setSearchType((e.target as HTMLInputElement).value)
            }
          >
            <div className="tabs-row">
              {tabs.slice(0, 2).map(type => (
                <label key={type} className="custom-radio-label">
                  <input
                    type="radio"
                    name="searchType"
                    value={type}
                    checked={searchType === type}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchType(e.target.value)}
                  />
                  <span className="custom-radio"></span>
                  {type}
                </label>
              ))}
            </div>
            <div className="tabs-row">
              {tabs.slice(2, 4).map(type => (
                <label key={type} className="custom-radio-label">
                  <input
                    type="radio"
                    name="searchType"
                    value={type}
                    checked={searchType === type}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchType(e.target.value)}
                  />
                  <span className="custom-radio"></span>
                  {type}
                </label>
              ))}
            </div>
          </div>
          <div className="filter-radio-group">
            {searchType && renderFilterOptions(searchType, selectedFilter, setSelectedFilter)}
          </div>
          <div>
            <div>
              {showTextarea ? (
                <div>
                  <div>
                    <label>Add your search list (separate items by line):</label>
                  </div>
                  <textarea
                    placeholder="Enter (one per line)"
                    value={text}
                    onChange={handleQueryChange}
                    className="filter-textarea as-textarea"
                    rows={5}
                    style={{ width: '100%' }}
                  />
                  {getItemCount(text) > 0 && (
                    <button
                      onClick={handleSearchClick}
                      className="filter-upload"
                      disabled={getItemCount(text) < 0}
                    >
                      {loading ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <LoadingSpinner />
                          Searching...
                        </span>
                      ) : (
                        'Search List'
                      )}
                    </button>
                  )}
                  {getItemCount(text) > 0 && (
                    <button onClick={clearListOnClick} className="filter-upload">
                      Clear List
                    </button>
                  )}
                  {showExamplesButton && getItemCount(text) === 0 && (
                    <button onClick={showExamplesOnClick} className="filter-upload">
                      Show Examples
                    </button>
                  )}
                  {(searchType === 'VARIANTS' || (searchType === 'GENES' && selectedFilter !== 'name')) && (
                    <button
                      onClick={handleEnrichmentRedirect}
                      className="filter-upload"
                      title="Go to Enrichment Analysis"
                    >
                      Enrichment Analysis
                    </button>
                  )}
                  <p style={{ margin: '10px' }}>Items: {getItemCount(text)}</p>{' '}
                </div>
              ) : (
                <div>
                  {renderSuggestions()}

                  <div className="error-message">
                    {!loading && suggestionsData.length === 0 && (
                      <p style={{ color: 'red', fontStyle: 'italic' }}>
                        No data found or the service is temporarily unavailable.
                      </p>
                    )}

                    {filteredNotFoundItems.length > 0 && (
                      <div>
                        <p style={{ color: 'red', fontStyle: 'italic' }}>
                          Input search does not follow the expected format for {searchType}{' '}
                          {getMsg(searchType, selectedFilter)}:
                        </p>
                        <ul style={{ color: 'red', fontStyle: 'italic' }}>
                          {Array.from(
                            new Map(filteredNotFoundItems.map((item: any) => [item.query, item])).values(),
                          ).map((uniqueItem: any, index: number) => (
                            <li key={index}>{uniqueItem.query}</li>
                          ))}
                        </ul>
                        Examples of {searchType} {getExpectedFormatMessage(searchType, selectedFilter)}
                      </div>
                    )}
                  </div>

                  <button onClick={handleBackClick} className="filter-upload">
                    Back
                  </button>
                  {selectedItems?.length > 0 && (
                    <button onClick={handleSearchSelectedItems} className="filter-upload">
                      Search Selected Items
                    </button>
                  )}
                  {selectedItems?.length > 0 && (
                    <button onClick={handleClearAll} className="filter-upload">
                      Clear Selection
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const allResults = suggestionsData?.flatMap((item: any) => item.results) || [];
                      if (selectedItems?.length === allResults.length) {
                        handleClearAll();
                      } else {
                        handleSelectAll();
                      }
                    }}
                    className="filter-upload"
                  >
                    Select All
                  </button>
                  <button onClick={() => handleDownload()} className="filter-upload">
                    Download Results (ZIP)
                  </button>
                  <p>
                    From the <strong>{totalSearched}</strong> unique input items, we found{' '}
                    <strong>{totalFound}</strong> in DISGENET.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdvancedSearch;
